import React, { useEffect, useState } from "react";
import {
  AiOutlineMinus,
  AiOutlinePlus,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import Icon from "../common/Icon";
import { useAuth } from "../contexts/AuthContext";
import FormButton from "../common/FormButton";
import GeneralButton from "../common/GeneralButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import FullScreenLoading from "../common/loading/FullScreenLoading";
import { ImCart } from "react-icons/im";
import { useCart } from "../contexts/CartContext"; // ✅ Import context
import { motion } from "framer-motion";
const Cart = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [showDetails, setShowDetails] = useState(false);
  // ✅ Destructure everything from CartContext
  const {
    cart,
    loading,
    itemLoading,
    fetchCart,
    updateCartQuantity,
    removeFromCart,
  } = useCart();

  // ✅ Fetch cart only once
  useEffect(() => {
    fetchCart();
  }, []);

  // 🔹 Handle quantity change
  const handleQuantityChange = (index, action) => {
    updateCartQuantity(index, action);
  };

  // 🔹 Remove item
  const deleteItem = (index) => {
    removeFromCart(index);
  };

  return (
    <>
   
      {loading && <FullScreenLoading />}
      {!isLoggedIn && cart?.orderCart?.items?.length > 0 && (
        <p className="text-28pt lg:text-34pt mb-4 md:mb-5 lg:mb-7">Cart</p>
      )}
      <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.5, ease: "easeOut" }}
        className={`${
          !cart || !cart?.orderCart || cart?.orderCart?.items?.length === 0
            ? ""
            : "bg-[#DEE2DD]/40"
        } flex flex-col gap-6  rounded-[15px] p-6 lg:px-12 lg:py-10 2xl:pb-10 w-full relative`}
      >
        {!cart || !cart?.orderCart || cart?.orderCart?.items?.length === 0 ? (
          <div className="flex flex-col items-center justify-center">
            <div className="w-full flex justify-center items-center py-10 px-4">
              <div className="bg-quaternary/10 border border-quaternary/40 text-quaternary px-6 py-4 rounded-md text-center max-w-xl w-full shadow-md flex flex-col items-center gap-2">
                <ImCart className="text-quaternary w-16 h-16" />
                <p className=" text-lg">Your cart looks empty</p>
                <p className="text-sm text-quaternary font-mono">
                  {" "}
                  Why don't you add something?
                </p>
                <GeneralButton
                  text="View Products"
                  title="Click to shop products"
                  className="mt-3"
                  bgColor="#A0AE3F"
                  onClick={() => navigate("/shop")}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className={
                isLoggedIn
                  ? "grid grid-cols-1 gap-6"
                  : "grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-6 2xl:gap-12 items-start"
              }
            >
              {/* 🧺 Cart Items Section */}
              <div>
                <div
                  className={
                    isLoggedIn
                      ? "grid grid-cols-1  gap-6 items-start"
                      : "grid grid-cols-1 xl:grid-cols-2 gap-6 items-start"
                  }
                >
                  <div
                    className={
                      isLoggedIn
                        ? "grid grid-cols-1 xl:grid-cols-2 gap-6 "
                        : "grid grid-cols-1 gap-6"
                    }
                  >
                    {cart?.orderCart?.items?.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between shadow-sm relative min-h-[130px]"
                      >
                        {/* Loading overlay */}
                        {itemLoading === index && (
                          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-lg">
                            <ContainerLoading />
                          </div>
                        )}

                        {/* Product Image */}
                        <img
                          src={
                            process.env.REACT_APP_BACKEND_URL +
                            "public/" +
                            item?.product?.image
                          }
                          alt={item.product?.title}
                          className="w-20 h-20 md:w-28 md:h-28 object-cover rounded-lg bg-gray mb-2 md:mb-0"
                          onError={(e) => {
                            e.currentTarget.src = require("../../assets/images/link.png");
                          }}
                        />

                        {/* Product Details */}
                        <div className="flex-1 flex flex-col justify-between md:ms-4 md:me-10">
                          <div>
                            <p className="text-base line-clamp-1">
                              {item.product?.title}
                            </p>
                            <p className="text-orange mt-1">
                              {item?.product?.salePrice} AED
                            </p>
                          </div>

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3 mt-4">
                            <button
                              title={
                                item.quantity > 1
                                  ? "Click to decrease product quantity"
                                  : "Minimum quantity reached"
                              }
                              onClick={() =>
                                item.quantity > 1 &&
                                handleQuantityChange(index, "sub")
                              }
                              disabled={item.quantity <= 1}
                              className={`rounded p-1 transition ${
                                item.quantity > 1
                                  ? "bg-lightGray hover:bg-gray"
                                  : "bg-gray-200 cursor-not-allowed opacity-60"
                              }`}
                            >
                              <AiOutlineMinus />
                            </button>

                            <span>{item.quantity}</span>

                            <button
                              title="Click to increase product quantity"
                              onClick={() => handleQuantityChange(index, "add")}
                              className="bg-lightGray rounded p-1 hover:bg-gray"
                            >
                              <AiOutlinePlus />
                            </button>
                          </div>
                        </div>

                        {/* Delete Button */}
                        <button
                          title="Click to remove item from cart"
                          onClick={() => deleteItem(index)}
                          className="absolute top-5 right-5 text-green hover:text-secondary"
                        >
                          <Icon icon="delete-product" size={25} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="">
                    {!isLoggedIn && (
                      <>
                        <div
                          className={`min-h-[130px] bg-white rounded-lg p-6 shadow-sm flex flex-col items-start `}
                        >
                          {/* 🔹 Expandable Details */}
                          <div
                            className={`overflow-hidden transition-all duration-500 ease-in-out ${
                              showDetails ? "max-h-[500px] mt-4" : "max-h-0"
                            } w-full`}
                          >
                            <div className="flex flex-col gap-3 mb-4 pb-4 border-b border-gray">
                              {cart?.orderCart?.items?.map((item, idx) => (
                                <div key={idx} className="flex justify-between">
                                  <div className="flex flex-col justify-between w-[70%]">
                                    {/* Product name on left */}
                                    <p className="text-sm  text-gray-800 truncate">
                                      {item.product?.title}
                                    </p>

                                    {/* Quantity × Price always visible on right */}
                                    <p className="text-[12px] font-mono  flex-shrink-0 whitespace-nowrap">
                                      ({item.quantity} ×{" "}
                                      {item.product?.salePrice} AED)
                                    </p>
                                  </div>

                                  <p className="text-right font-medium">
                                    {item.quantity * item.product?.salePrice}{" "}
                                    AED
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div
                            className={`bg-white rounded-lg  flex flex-row items-center justify-between w-full`}
                          >
                            <p className="text-21pt text-orange">Total</p>
                            <p className="text-21pt text-orange">
                              <span className="text-orange">
                                {cart?.orderCart?.invoice?.subTotal || 0} AED
                              </span>
                            </p>
                          </div>
                          <p
                            className="text-16pt text-green font-mono mt-4 cursor-pointer hover:text-secondary transition"
                            onClick={() => setShowDetails(!showDetails)}
                          >
                            {showDetails ? "Hide details" : "Show details"}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Cart Summary */}
                {isLoggedIn && (
                  <div
                    className={`mt-0 bg-white rounded-lg p-6 shadow-sm flex flex-col md:flex-row items-center ${
                      isLoggedIn ? "justify-between" : "justify-end"
                    }`}
                  >
                    <p className="text-lg text-orange">
                      Total:{" "}
                      <span className="text-orange">
                        {cart?.orderCart?.invoice?.subTotal || 0} AED
                      </span>
                    </p>

                    <GeneralButton
                      text="Checkout"
                      title="Click here to order"
                      className="text-20pt mt-2 md:mt-0"
                      textColor="#fff"
                      onClick={() => navigate("/checkout/order")}
                    />
                  </div>
                )}
              </div>

              {/* Login / Signup Buttons */}
              {!isLoggedIn && (
                <div className="grid grid-cols-1 gap-4 self-start ">
                  <FormButton
                    text="Login"
                    onClick={() => navigate("/signin?from=cart")}
                  />
                  <FormButton
                    text="Signup"
                    onClick={() => navigate("/signup?from=cart")}
                  />
                  <FormButton
                    text="Express Checkout"
                    onClick={() => navigate("/express-checkout")}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </>
  );
};

export default Cart;
